/**
 * Collaborator filter interface for type-safe query keys
 * 
 * @property projectId - Filter by project ID
 * @property limit - Limit the number of results
 * @property includeInvitations - Whether to include pending invitations
 */
export interface CollaboratorFilters {
  projectId?: string;
  limit?: number;
  includeInvitations?: boolean;
}

/**
 * Query key factory for collaborators
 * 
 * These keys are used to identify and organize React Query cache entries
 * related to project collaborators. Following TanStack Query best practices for structured keys.
 * 
 * Query keys are organized hierarchically to allow for selective invalidation:
 * - collaboratorKeys.all: Invalidates all collaborator-related queries
 * - collaboratorKeys.lists(): Invalidates all collaborator list queries
 * - collaboratorKeys.list(filters): Invalidates specific filtered lists
 * - collaboratorKeys.invitations(): Invalidates all invitation queries
 * - collaboratorKeys.invitation(projectId): Invalidates invitations for a specific project
 * 
 * Usage examples:
 * - collaboratorKeys.all - Base key for all collaborator data
 * - collaboratorKeys.lists() - Key for all collaborator lists
 * - collaboratorKeys.list({ projectId: 'proj-123' }) - Key for collaborators of a specific project
 * - collaboratorKeys.invitations() - Key for all invitation queries
 * - collaboratorKeys.invitation('proj-123') - Key for invitations of a specific project
 * 
 * @example
 * // Invalidate all collaborator data
 * queryClient.invalidateQueries({ queryKey: collaboratorKeys.all });
 * 
 * @example
 * // Fetch collaborators for a specific project
 * useQuery({ queryKey: collaboratorKeys.list({ projectId: 'proj-123' }), queryFn: fetchCollaborators });
 */
export const collaboratorKeys = {
  /**
   * Base key for all collaborator-related queries
   * Invalidating this key will invalidate all collaborator queries
   */
  all: ['collaborators'] as const,
  
  /**
   * Keys for all collaborator lists
   * Invalidating this key will invalidate all list queries but not detail queries
   * 
   * @returns Tuple of ['collaborators', 'list']
   */
  lists: () => [...collaboratorKeys.all, 'list'] as const,
  
  /**
   * Key for filtered collaborator lists
   * Used for storing and retrieving specific filtered views of collaborators
   * 
   * @param filters - Object containing filter criteria
   * @returns Tuple of ['collaborators', 'list', {...filters}]
   * 
   * @example
   * // Get collaborators for a specific project
   * collaboratorKeys.list({ projectId: 'proj-123' })
   */
  list: (filters: CollaboratorFilters = {}) => [...collaboratorKeys.lists(), filters] as const,
  
  /**
   * Keys for all invitation queries
   * Invalidating this will invalidate all invitation queries
   * 
   * @returns Tuple of ['collaborators', 'invitations']
   */
  invitations: () => [...collaboratorKeys.all, 'invitations'] as const,
  
  /**
   * Key for invitations by project ID
   * Used for storing and retrieving invitations for a specific project
   * 
   * @param projectId - Project ID
   * @returns Tuple of ['collaborators', 'invitations', projectId]
   * 
   * @example
   * // Get invitations for project with ID "proj-123"
   * collaboratorKeys.invitation('proj-123')
   */
  invitation: (projectId: string) => [...collaboratorKeys.invitations(), projectId] as const,
  
  /**
   * Key for project has collaborators check
   * Used for checking if a project has any collaborators
   * 
   * @returns Tuple of ['collaborators', 'has-collaborators']
   */
  hasCollaborators: () => [...collaboratorKeys.all, 'has-collaborators'] as const,
  
  /**
   * Key for checking if a specific project has collaborators
   * 
   * @param projectId - Project ID
   * @returns Tuple of ['collaborators', 'has-collaborators', projectId]
   */
  hasCollaboratorsForProject: (projectId: string) => [...collaboratorKeys.hasCollaborators(), projectId] as const,
};